import React from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  StethoscopeIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ShieldIcon,
  AwardIcon,
  AlertTriangleIcon,
  LightbulbIcon,
  ActivityIcon,
  ZapIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { openWhatsApp } from "../../lib/utils";

const Piles = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Piles Treatment", href: "/piles" }
  ];

  const stats = [
    { number: "96%", label: "Success Rate", icon: ShieldIcon },
    { number: "20min", label: "Procedure Time", icon: ClockIcon },
    { number: "1 Day", label: "Recovery Time", icon: ActivityIcon },
    { number: "800+", label: "Successful Cases", icon: AwardIcon }
  ];

  const causes = [
    "Chronic constipation and straining during bowel movements",
    "Prolonged sitting on toilet seat",
    "Heavy lifting and physical exertion",
    "Pregnancy and childbirth",
    "Obesity and sedentary lifestyle",
    "Low fiber diet and dehydration",
    "Age-related weakening of tissues",
    "Family history of piles"
  ];

  const symptoms = [
    "Bright red blood during bowel movements",
    "Pain and discomfort around anus",
    "Itching and irritation in anal area",
    "Swelling or lumps around anus",
    "Mucus discharge from anus",
    "Feeling of incomplete bowel evacuation",
    "Pain while sitting or walking",
    "Prolapsed hemorrhoids (external)"
  ];

  const procedures = [
    {
      name: "Rubber Band Ligation",
      description: "Non-surgical treatment using rubber bands to cut off blood supply",
      duration: "15-20 minutes",
      recovery: "Same day",
      benefits: ["No anesthesia needed", "Quick procedure", "Minimal discomfort", "High success rate"]
    },
    {
      name: "Infrared Coagulation",
      description: "Heat treatment to shrink internal hemorrhoids",
      duration: "10-15 minutes",
      recovery: "Same day",
      benefits: ["Painless procedure", "No side effects", "Quick recovery", "Effective for grades 1-2"]
    },
    {
      name: "Sclerotherapy",
      description: "Injection of chemical solution to shrink hemorrhoids",
      duration: "15-20 minutes",
      recovery: "Same day",
      benefits: ["Non-invasive", "Minimal pain", "Quick procedure", "Suitable for small piles"]
    },
    {
      name: "Laser Hemorrhoidectomy",
      description: "Advanced laser surgery for severe hemorrhoids",
      duration: "30-45 minutes",
      recovery: "1-2 days",
      benefits: ["Precise removal", "Minimal bleeding", "Fast healing", "Less post-op pain"]
    }
  ];

  const prevention = [
    "Eat high-fiber foods (fruits, vegetables, whole grains)",
    "Drink plenty of water (8-10 glasses daily)",
    "Avoid straining during bowel movements",
    "Exercise regularly to improve circulation",
    "Don't sit on toilet for extended periods",
    "Maintain healthy weight",
    "Avoid heavy lifting when possible",
    "Practice good anal hygiene"
  ];

  const diagnosis = [
    "Digital rectal examination (DRE)",
    "Anoscopy for internal examination",
    "Proctoscopy for detailed visualization",
    "Sigmoidoscopy for lower colon check",
    "Colonoscopy if bleeding is significant",
    "Stool test for blood detection",
    "Physical examination of external hemorrhoids",
    "Grading of hemorrhoid severity (1-4)"
  ];

  const pilesStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": "Piles Treatment",
    "description": "Advanced piles treatment using minimally invasive techniques. Expert care for hemorrhoids with modern surgical methods and comprehensive recovery support.",
    "procedureType": "Surgical Treatment",
    "bodyLocation": "Anus and Rectum",
    "preparation": "Dietary modifications and bowel preparation",
    "followup": "Regular follow-up visits and wound care",
    "provider": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Delhi",
        "addressCountry": "IN"
      }
    },
    "medicalSpecialty": "General Surgery",
    "areaServed": ["Delhi", "Gurgaon", "Noida", "Faridabad"]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Piles Treatment"
        description="Expert piles treatment at TheLivCure. Advanced minimally invasive surgical techniques for hemorrhoids. Experienced surgeons, modern facilities, comprehensive care. Book consultation today."
        keywords="piles treatment, hemorrhoids surgery, piles surgery Delhi, hemorrhoids treatment, piles doctor, piles specialist, piles cure, hemorrhoids cure, piles surgery cost"
        url="/piles"
        structuredData={pilesStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-purple-50 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-8">
            <div>
              <Badge className="bg-purple-100 text-purple-800 mb-4">Proctology</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Advanced Piles Treatment
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
                Expert piles treatment with minimally invasive procedures. Get relief from 
                hemorrhoid pain and bleeding with our advanced treatment options including 
                rubber band ligation, laser surgery, and sclerotherapy.
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed">
                Piles, also known as hemorrhoids, are swollen blood vessels in the anal and rectal area that can cause 
                significant discomfort, pain, and bleeding. Our proctology specialists use advanced techniques to provide 
                effective treatment with minimal downtime and excellent results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <BookAppointmentModal>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-lg">
                    Book Free Consultation
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </BookAppointmentModal>
                <Button 
                  variant="outline" 
                  className="border-purple-600 text-purple-600 hover:bg-purple-50 px-8 py-3 text-lg"
                  onClick={() => openWhatsApp("9311720045", "Hi, I need consultation for piles treatment")}
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-8 text-white mb-8">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <stat.icon className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-2xl sm:text-3xl font-bold">{stat.number}</div>
                      <div className="text-purple-100 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src="/image/piles_1.jpg" 
                    alt="Piles treatment procedure" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Advanced minimally invasive piles treatment
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Piles Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Piles (Hemorrhoids)</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Piles are swollen and inflamed veins in the rectum and anus that can cause discomfort, 
                bleeding, and pain. They are very common and affect millions of people worldwide, 
                especially those over 50 years of age.
              </p>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">
                There are two main types of piles: internal hemorrhoids, which develop inside the rectum, 
                and external hemorrhoids, which form under the skin around the anus. Both types can cause 
                significant discomfort and require appropriate treatment.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Internal Piles</h3>
                    <p className="text-gray-600">Located inside the rectum, usually painless but may bleed</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">External Piles</h3>
                    <p className="text-gray-600">Form under the skin around the anus, often painful and itchy</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Thrombosed Piles</h3>
                    <p className="text-gray-600">External piles with blood clots, causing severe pain</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/piles_2.jpg" 
                alt="Piles anatomy and treatment" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Understanding piles anatomy and treatment options
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms and Severity Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recognizing Piles Symptoms</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Piles symptoms can range from mild discomfort to severe pain and bleeding. 
              Early recognition and treatment can prevent complications and improve quality of life.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Rectal Bleeding</h3>
              <p className="text-sm text-gray-600">Bright red blood on toilet paper or in toilet bowl</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangleIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Pain & Discomfort</h3>
              <p className="text-sm text-gray-600">Pain, burning, or aching around the anal area</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ActivityIcon className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Itching & Irritation</h3>
              <p className="text-sm text-gray-600">Persistent itching and irritation in anal region</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Swelling & Lumps</h3>
              <p className="text-sm text-gray-600">Visible or palpable lumps around the anus</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Advanced Treatment Options Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Treatment Options</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our proctology specialists offer a range of advanced, minimally invasive treatment options 
                for piles, ensuring effective relief with minimal discomfort and faster recovery.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ZapIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Laser Treatment</h3>
                    <p className="text-gray-600">Advanced laser technology for precise and painless piles removal</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ShieldIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rubber Band Ligation</h3>
                    <p className="text-gray-600">Non-surgical procedure to cut off blood supply to hemorrhoids</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <AwardIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Sclerotherapy</h3>
                    <p className="text-gray-600">Chemical injection to shrink hemorrhoids effectively</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/piles_3.jpg" 
                alt="Advanced piles treatment equipment" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Modern proctology treatment equipment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="causes">Causes</TabsTrigger>
              <TabsTrigger value="symptoms">Symptoms</TabsTrigger>
              <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
              <TabsTrigger value="procedures">Procedures</TabsTrigger>
              <TabsTrigger value="prevention">Prevention</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <StethoscopeIcon className="w-6 h-6 mr-2 text-purple-600" />
                      Treatment Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-6">
                      Piles (hemorrhoids) are swollen blood vessels in the anal and rectal area. 
                      Our advanced treatment options include non-surgical procedures like rubber 
                      band ligation and laser surgery for effective relief with minimal discomfort.
                    </p>
                    <p className="text-gray-600 mb-6">
                      Hemorrhoids are very common, affecting millions of people worldwide. They can 
                      cause significant discomfort, bleeding, and impact daily life. Our proctology 
                      specialists use the latest minimally invasive techniques to provide effective 
                      treatment with excellent outcomes.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AwardIcon className="w-6 h-6 mr-2 text-purple-600" />
                      Why Choose Our Treatment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Expert Proctologists</h4>
                          <p className="text-sm text-gray-600">Specialized doctors with extensive piles treatment experience</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Advanced Techniques</h4>
                          <p className="text-sm text-gray-600">Latest laser, band ligation, and sclerotherapy methods</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Comfortable Care</h4>
                          <p className="text-sm text-gray-600">Pain-free procedures with minimal downtime</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <ClockIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Quick Recovery</h3>
                  <p className="text-sm text-gray-600">Most patients return to normal activities the same day</p>
                </div>
                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <ShieldIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">High Success Rate</h3>
                  <p className="text-sm text-gray-600">96% success rate with our minimally invasive procedures</p>
                </div>
                <div className="text-center p-6 bg-purple-50 rounded-lg sm:col-span-2 lg:col-span-1">
                  <ZapIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Minimally Invasive</h3>
                  <p className="text-sm text-gray-600">Advanced laser and band ligation techniques</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="causes" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangleIcon className="w-6 h-6 mr-2 text-orange-600" />
                    Common Causes of Piles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {causes.map((cause, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{cause}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="symptoms" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                    Symptoms to Watch For
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {symptoms.map((symptom, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{symptom}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="diagnosis" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ActivityIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Diagnostic Methods
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {diagnosis.map((test, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{test}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="procedures" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {procedures.map((procedure, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{procedure.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{procedure.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Duration:</span>
                          <span className="text-sm font-medium">{procedure.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Recovery:</span>
                          <span className="text-sm font-medium">{procedure.recovery}</span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-2">Benefits:</h4>
                        <ul className="space-y-1">
                          {procedure.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <CheckCircleIcon className="w-4 h-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ShieldIcon className="w-6 h-6 mr-2 text-purple-600" />
                    Treatment Process Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Consultation</h4>
                      <p className="text-sm text-gray-600">Detailed examination and treatment planning</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Procedure</h4>
                      <p className="text-sm text-gray-600">Minimally invasive treatment execution</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Recovery</h4>
                      <p className="text-sm text-gray-600">Brief monitoring and discharge</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-purple-600 font-bold text-lg">4</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Follow-up</h4>
                      <p className="text-sm text-gray-600">Post-treatment care and prevention tips</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="prevention" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                    Prevention Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {prevention.map((tip, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Piles Treatment Gallery</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See our advanced piles treatment facilities and procedures in action. 
              Our modern equipment and expert care ensure the best outcomes for our patients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/piles_1.jpg" 
                alt="Piles treatment procedure overview" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Overview</h3>
                <p className="text-sm text-gray-600">Advanced minimally invasive piles treatment procedures</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/piles_2.jpg" 
                alt="Piles anatomy and diagnosis" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Anatomy & Diagnosis</h3>
                <p className="text-sm text-gray-600">Understanding hemorrhoid anatomy and formation</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/piles_3.jpg" 
                alt="Modern surgical equipment" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Modern Equipment</h3>
                <p className="text-sm text-gray-600">State-of-the-art laser and surgical equipment</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/piles_4.jpg" 
                alt="Post-treatment care" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Post-Treatment Care</h3>
                <p className="text-sm text-gray-600">Comprehensive follow-up and recovery support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-purple-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Relief from Piles?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Book your consultation today and take the first step towards a comfortable life.
          </p>
          <BookAppointmentModal>
            <Button className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-3 text-lg">
              Book Free Consultation
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </BookAppointmentModal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Piles;
